import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Typography,
  FormControlLabel,
  Radio,
  Stack,
  Grid,
  RadioGroup,
  Card,
  TextField,
  InputAdornment,
} from '@mui/material';

import Scrollbar from '../../../components/Scrollbar';
import { FormProvider, RHFTextField } from 'src/components/hook-form';
import { useForm } from 'react-hook-form';
import moment from 'moment';
import { AnnouncingClosureShop, isHolidyOpen } from '../../../services/shopServices';
import { snackActions } from 'src/components/notistack/SnackbarUtilsConfigurator';
import Dialog from '@mui/material/Dialog';
import { LoadingButton } from '@mui/lab';
import StorefrontIcon from '@mui/icons-material/Storefront';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

const radioOptions = [
  { label: 'today', name: 'برای امروز' },
  { label: 'custom', name: 'تعداد دقیقه دلخواه' },
  { label: 'week', name: 'برای یک هفته' },
  { label: 'year', name: 'تا اطلاع ثانوی' },
];

export default function NotificationsPopover({ rootShopId, statusInquiry, onStatusChange }) {
  const [open, setOpen] = useState(null);
  const [selectedRadio, setSelectedRadio] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [localStatusInquiry, setLocalStatusInquiry] = useState(statusInquiry);
  const [isUpdating, setIsUpdating] = useState(false);
  const [customMinutes, setCustomMinutes] = useState('');
  const [customMinutesError, setCustomMinutesError] = useState('');

  useEffect(() => {
    setLocalStatusInquiry(statusInquiry);
  }, [statusInquiry]);

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
    setSelectedRadio('');
    setCustomMinutes('');
    setCustomMinutesError('');
  };

  useEffect(() => {
    const today = moment();
    let newStart = '';
    let newEnd = '';

    if (selectedRadio === 'today') {
      newStart = today.format('YYYY-MM-DD HH:mm:ss');
      newEnd = today.clone().add(1, 'day').startOf('day').add(4, 'hours').format('YYYY-MM-DD HH:mm:ss');
    } else if (selectedRadio === 'custom') {
      newStart = today.format('YYYY-MM-DD HH:mm:ss');
      newEnd = '';
    } else if (selectedRadio === 'week') {
      newStart = today.format('YYYY-MM-DD HH:mm:ss');
      newEnd = today.clone().add(1, 'week').endOf('day').format('YYYY-MM-DD HH:mm:ss');
    } else if (selectedRadio === 'year') {
      newStart = today.format('YYYY-MM-DD HH:mm:ss');
      newEnd = today.clone().add(10, 'year').endOf('day').format('YYYY-MM-DD HH:mm:ss');
    }
    setStart(newStart);
    setEnd(newEnd);
  }, [selectedRadio]);

  const handleRadioChange = (event) => {
    setSelectedRadio(event.target.value);
    setCustomMinutes('');
    setCustomMinutesError('');
  };

  const handleCustomMinutesChange = (e) => {
    const value = e.target.value;
    if (value === '' || (/^\d+$/.test(value) && parseInt(value) > 0)) {
      setCustomMinutes(value);
      setCustomMinutesError('');
    }
  };

  const methods = useForm({
    defaultValues: {
      descriptions: '',
    },
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    reset,
    setError,
  } = methods;

  const validateDescription = (value) => {
    const trimmed = value?.trim() || '';
    if (!trimmed) {
      return 'وارد کردن علت تعطیلی اجباری است';
    }
    if (trimmed.length < 3) {
      return 'علت تعطیلی باید حداقل ۳ کاراکتر باشد';
    }
    return true;
  };

  const onSubmit = async (values) => {
    if (!selectedRadio) {
      snackActions.error('لطفا یک بازه زمانی انتخاب کنید');
      return;
    }

    if (selectedRadio === 'custom') {
      const mins = parseInt(customMinutes);
      if (!customMinutes || isNaN(mins) || mins <= 0) {
        setCustomMinutesError('لطفا تعداد دقیقه معتبر وارد کنید');
        return;
      }
    }

    const descriptionValidation = validateDescription(values.descriptions);
    if (descriptionValidation !== true) {
      setError('descriptions', { type: 'manual', message: descriptionValidation });
      return;
    }

    let finalStart = start;
    let finalEnd = end;

    let radioValue = selectedRadio;

    if (selectedRadio === 'custom') {
      const mins = parseInt(customMinutes);
      const today = moment();
      finalStart = today.format('YYYY-MM-DD HH:mm:ss');
      finalEnd = today.clone().add(mins, 'minutes').format('YYYY-MM-DD HH:mm:ss');
      radioValue = String(mins); 
    }

    values.start = finalStart;
    values.end = finalEnd;
    values.descriptions = values.descriptions.trim(); 

    setIsUpdating(true);

    try {
      let response = await AnnouncingClosureShop(rootShopId, values, radioValue);

      if (response.status === 200 || response.status === 201) {
        snackActions.success(response?.data?.message || 'فروشگاه با موفقیت تعطیل شد');
        handleClose();
        reset();
        onStatusChange();
      } else {
        snackActions.error('خطا در تعطیلی فروشگاه');
      }
    } catch (error) {
      console.error('Error:', error);
      snackActions.error('خطا در ارسال درخواست');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleIsHoliday = async () => {
    setIsUpdating(true);

    try {
      let response = await isHolidyOpen(rootShopId);

      if (response.status === 200) {
        snackActions.success(response?.data?.message || 'فروشگاه با موفقیت باز شد');
        handleClose();
        onStatusChange();
      } else {
        snackActions.error('خطا در باز کردن فروشگاه');
      }
    } catch (error) {
      console.error('Error:', error);
      snackActions.error('خطا در باز کردن فروشگاه');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <>
      <Grid container>
        <Grid item xs="auto">
          <LoadingButton
            variant="outlined"
            onClick={handleOpen}
            color={!localStatusInquiry?.is_holiday ? 'success' : 'error'}
            loading={isUpdating}
            sx={{
              minWidth: { xs: 100, sm: 140 },
              height: { xs: 36, sm: 42 },
              fontWeight: 500,
              fontSize: { xs: '0.813rem', sm: '0.875rem' },
              px: { xs: 1.5, sm: 2 },
            }}
          >
            {!localStatusInquiry?.is_holiday ? 'تعطیلی واحد' : 'باز کردن واحد'}
          </LoadingButton>
        </Grid>
      </Grid>

      {localStatusInquiry?.is_holiday ? (
        <Dialog
          open={Boolean(open)}
          onClose={handleClose}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: 24,
            },
          }}
        >
          <Box sx={{ p: 3 }}>
            <Stack spacing={2.5} alignItems="center">
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  bgcolor: 'success.dark',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1,
                }}
              >
                <StorefrontIcon style={{ width: 32, height: 32, color: '#FFF' }} />
              </Box>

              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  textAlign: 'center',
                  color: 'text.primary',
                }}
              >
                باز کردن "{localStatusInquiry?.name}"
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  textAlign: 'center',
                  color: 'text.secondary',
                  px: 2,
                }}
              >
                آیا می‌خواهید فروشگاه را باز کنید؟
              </Typography>

              <Stack direction="row" spacing={1.5} sx={{ width: '100%', pt: 1 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="inherit"
                  onClick={handleClose}
                  sx={{
                    height: 44,
                    fontWeight: 500,
                  }}
                >
                  انصراف
                </Button>

                <LoadingButton
                  fullWidth
                  variant="contained"
                  color="success"
                  onClick={handleIsHoliday}
                  loading={isUpdating}
                  sx={{
                    height: 44,
                    fontWeight: 600,
                  }}
                >
                  بله، مطمئنم
                </LoadingButton>
              </Stack>
            </Stack>
          </Box>
        </Dialog>
      ) : (
        <Dialog
          open={Boolean(open)}
          onClose={handleClose}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: 24,
            },
          }}
        >
          <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ p: 3 }}>
              <Stack spacing={3}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      bgcolor: 'error.lighter',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <HighlightOffIcon style={{ width: 24, height: 24, color: 'red' }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      اعلام تعطیلی فروشگاه
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      فروشگاه "{localStatusInquiry?.name}" را تعطیل کنید
                    </Typography>
                  </Box>
                </Stack>

                <Divider />

                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    مدت زمان تعطیلی
                  </Typography>
                  <RadioGroup value={selectedRadio} onChange={handleRadioChange}>
                    <Stack spacing={1}>
                      {radioOptions.map((option) =>
                        option.label === 'custom' ? (
                          <Card
                            key={option.label}
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              border: '1px solid',
                              borderColor: selectedRadio === 'custom' ? 'primary.main' : 'divider',
                              bgcolor: selectedRadio === 'custom' ? 'primary.lighter' : 'transparent',
                              transition: 'all 0.2s',
                            }}
                          >
                            <Stack spacing={1}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {option.name}
                              </Typography>
                              <TextField
                                fullWidth
                                size="small"
                                // placeholder="برای مثال ۳۰"
                                value={customMinutes}
                                onChange={(e) => {
                                  handleCustomMinutesChange(e);
                                  if (e.target.value) setSelectedRadio('custom');
                                }}
                                onFocus={() => setSelectedRadio('custom')}
                                error={Boolean(customMinutesError)}
                                helperText={customMinutesError || ''}
                                inputProps={{
                                  inputMode: 'numeric',
                                  min: 1,
                                  style: { direction: 'ltr', textAlign: 'center' },
                                }}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <Typography variant="caption" color="text.secondary">
                                        دقیقه
                                      </Typography>
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </Stack>
                          </Card>
                        ) : (
                          <Card
                            key={option.label}
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              cursor: 'pointer',
                              border: '1px solid',
                              borderColor: selectedRadio === option.label ? 'primary.main' : 'divider',
                              bgcolor: selectedRadio === option.label ? 'primary.lighter' : 'transparent',
                              transition: 'all 0.2s',
                              '&:hover': {
                                borderColor: 'primary.main',
                                bgcolor: 'primary.lighter',
                              },
                            }}
                            onClick={() => setSelectedRadio(option.label)}
                          >
                            <FormControlLabel
                              value={option.label}
                              control={<Radio />}
                              label={
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {option.name}
                                </Typography>
                              }
                              sx={{ width: '100%', m: 0 }}
                            />
                          </Card>
                        )
                      )}
                    </Stack>
                  </RadioGroup>
                </Box>

                <RHFTextField
                  name="descriptions"
                  label="علت تعطیلی *"
                  multiline
                  rows={3}
                  placeholder="دلیل تعطیلی را وارد کنید..."
                  rules={{
                    validate: validateDescription,
                  }}
                />

                <Stack direction="row" spacing={1.5}>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="inherit"
                    onClick={handleClose}
                    sx={{
                      height: 44,
                      fontWeight: 500,
                    }}
                  >
                    انصراف
                  </Button>

                  <LoadingButton
                    fullWidth
                    type="submit"
                    variant="contained"
                    color="error"
                    loading={isUpdating}
                    disabled={!selectedRadio}
                    sx={{
                      height: 44,
                      fontWeight: 600,
                    }}
                  >
                    تعطیل کردن
                  </LoadingButton>
                </Stack>
              </Stack>
            </Box>
          </FormProvider>
        </Dialog>
      )}
    </>
  );
}